import React, { ReactElement, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faPen } from '@fortawesome/free-solid-svg-icons'
library.add(faPen)
import { actionPending, actionQueryCompleted, conditionalDispatch } from '../helpers/action/action'
import { DEFAULT_ITEMS_PER_PAGE, infiniteScrollConfig as infiniteScrollConfigOrig, LISTEN_TO_WINDOW } from './infinitescroll/helpers'
import { usePersistedViewLength, useScrollRestoration } from './infinitescroll/hooks'
import { InfiniteScrollContextProvided, InfiniteScrollHeaderProps, InfiniteScrollProps } from './InfiniteScroll.d'
import { LoadDetailsAction, PrimaryAction } from '../helpers/action/Action.d'
import ReactInfiniteScroller from 'react-infinite-scroller'
import { EmptySearchError } from '../common/ErrorMessage'
import Loading from './Loading'
import { formatDate } from '../helpers/format'
import { PersonContext } from '../contexts/PersonContext'
import { EntryId } from '../Entry/Entry.d'
import { EntryContext } from '../contexts/EntryContext'
import { entrySpanDates } from '../Entry/EntrySpans'

export const infiniteScrollConfig = infiniteScrollConfigOrig

export const InfiniteScrollHeader = (props: InfiniteScrollHeaderProps): ReactElement => {
    if (!props.value) {
        return <h3>Sorted by <em>{props.field}</em>:</h3>
    } else if (props.field === 'span') {
        return <InfiniteScrollSpanHeader spanId={ Number(props.value) } />
    } else if (props.field === 'person') {
        return <InfiniteScrollPersonHeader personId={ Number(props.value) } linkText={ props.personLinkText } />
    } else if (props.field === 'date' && props.comparison === 'after') {
        return <h3>Most mentioned since <em>{formatDate(props.value, 'long')}</em>:</h3>
    } else if (props.comparison === 'between') {
        const [from, until] = (props.value || '').split(';')
        if (!from || !until) {
            return <InfiniteScrollHeader { ...props } comparison="after" value={ from || until || undefined } />
        }
        if (props.field === 'date') {
            return <h3>Most mentioned between <em>{formatDate(from, 'long')}</em> and <em>{formatDate(until, 'long')}</em>:</h3>
        } else {
            return <h3>With <em>{props.field?.replaceAll('_', ' ')}</em> between <em>{from}</em> and <em>{until}</em>:</h3>
        }
    } else {
        return <h3>With <em>{props.field?.replaceAll('_', ' ')}</em> {props.comparison || 'of'} <em>{props.value}</em>:</h3>
    }
}

export const InfiniteScrollSpanHeader = ({ spanId, onEdit }: { spanId: EntryId, onEdit?: () => void }): ReactElement => {
    const { state } = useContext(EntryContext)
    const span = state.spans?.[spanId]
    const editIcon = onEdit && <>{' '}<FontAwesomeIcon icon="pen" className="clickable" onClick={ onEdit } /></>
    if (!span) {
        return <h3>During <em>span #{spanId}</em>{editIcon}</h3>
    }
    const dates = entrySpanDates(span)
    if (span.title) {
        return <h3>During <em>{span.title}</em> ({dates}):{editIcon}</h3>
    }
    return <h3>{span.endTimestamp ? 'From' : 'On'} <em>{dates}</em>{editIcon}</h3>
}
const InfiniteScrollPersonHeader = ({ personId, linkText }: { personId: number, linkText: string }): ReactElement => {
    const { state: people } = useContext(PersonContext)
    return <h3>{linkText} <em>{people.people?.[Number(personId)]?.name || 'person #' + personId}</em>:</h3>
}

/**
 * This component does all the legwork of outputting unlimited lists of 'items',
 *  including dealing with scrolling down, sending API requests, retrieving data
 *  from context state and stopping when no more data are available.
 */
const InfiniteScroll = (props: InfiniteScrollProps): ReactElement => {
    // Read from the provided context.
    const { state, dispatch } = useContext<InfiniteScrollContextProvided>(props.context)
    const itemsPerPage = props.itemsPerPage || DEFAULT_ITEMS_PER_PAGE

    const produceIdsKey = JSON.stringify(props.produceIds)
    const storageKey = `trove_infinitescroll_${produceIdsKey}${props.maxItems !== undefined ? `_max${props.maxItems}` : ''}`

    const [currentViewLength, setCurrentViewLength] = usePersistedViewLength(storageKey, itemsPerPage)
    // Make sure to prevent duplicate requests being sent through race conditions while we wait
    const [paused, setPaused] = React.useState<boolean>(false)

    const notSkipped = props.skippedItems ? (id: number) => !props.skippedItems?.includes(id) : () => true
    const knownIds = state.idsByQuery?.[produceIdsKey] || []
    const idsReadyToRender = knownIds.filter(id => props.itemCanBeRendered(id)).filter(notSkipped)
    const idsRenderedNow = idsReadyToRender.slice(0, currentViewLength)

    // Restore scroll position once enough items are rendered.
    // Requires at least one item to be present so we don't fire before the DOM has any height.
    // Fires when we have currentViewLength renderable items, or all available items are shown.
    const readyToRestoreScroll = idsRenderedNow.length > 0 && (
        idsRenderedNow.length >= currentViewLength
        || (idsRenderedNow.length === idsReadyToRender.length && actionQueryCompleted(produceIdsKey, state)))
    useScrollRestoration(storageKey, readyToRestoreScroll)

    if (props.maxItems && currentViewLength > props.maxItems) {
        setCurrentViewLength(props.maxItems)
        return <></>
    }

    /** Dispatch an action only if it isn't currently in progress */
    const dispatchSafely = (action: PrimaryAction, key: string | PrimaryAction = JSON.stringify(action)) => {
        if (paused || actionPending(key, state) || actionQueryCompleted(key, state))
            return
        setPaused(true) // prevents other actions on future renders before the timer has fired
        setTimeout(() => {
            conditionalDispatch(action, state, dispatch, { key })
            setPaused(false)
        }, 1)
    }

    // Retrieve any IDs which are either missing or potentially out-of-date
    if (!actionQueryCompleted(produceIdsKey, state)) {
        // If we refreshed the page, start retrieving IDs from the beginning again in case they changed
        if (state.actionStatus?.[produceIdsKey] === undefined) {
            dispatchSafely({ ...props.produceIds, offset: 0 }, produceIdsKey)
        } else {
            // If we've already retrieved some IDs, retrieve more if/when necessary
            if (currentViewLength - knownIds.length > 0) {
                dispatchSafely({ ...props.produceIds, offset: knownIds.length }, produceIdsKey)
            }
        }
    }

    // Helper functions for retrieving item details
    const detailsAction = (pageStartIndex: number): LoadDetailsAction => ({
        type: props.requestDetailsAction,
        items: knownIds.slice(pageStartIndex, pageStartIndex + itemsPerPage)
    })
    const detailsActionComplete = (pageStartIndex: number): boolean => actionQueryCompleted(detailsAction(pageStartIndex), state)

    /** The start index of the latest chunk of details which haven't yet been retrieved since page load */
    let latestPageStartIndex = currentViewLength - itemsPerPage
      + (currentViewLength % itemsPerPage === 0 ? 0 : itemsPerPage - currentViewLength % itemsPerPage)
    // Retrieve any missing / out-of-date details, in chunks of size itemsPerPage
    if (detailsAction(latestPageStartIndex).items.length > 0 && !detailsActionComplete(latestPageStartIndex)) {
        // It's possible to reload the page while offline, then scroll down to increase currentViewLength, then go online.
        // In this case, we want to redo all the detailsActions from the start: count backward to find the next one to run.
        while (latestPageStartIndex > 0 && !detailsActionComplete(latestPageStartIndex - itemsPerPage)) {
            latestPageStartIndex -= itemsPerPage
        }
        dispatchSafely(detailsAction(latestPageStartIndex))
    }

    /** Function called when scrolling down, if hasMore is true */
    const loadMore = () => {
        if (knownIds.length > currentViewLength || (knownIds.length <= currentViewLength && !actionQueryCompleted(produceIdsKey, state))) {
            const newViewLength = Math.min(
                idsRenderedNow.length + itemsPerPage, // NB isRenderedNow is capped at currentViewLength
                props.maxItems || Number.POSITIVE_INFINITY)
            if (newViewLength > currentViewLength) {
                setCurrentViewLength(newViewLength)
            }
        }
    }

    const alreadyShowingMaxItems = props.maxItems !== undefined
      && idsRenderedNow.length >= props.maxItems

    /** if hasMore, the Loading bar will be shown and scroll events will be listened for */
    const hasMore = !alreadyShowingMaxItems
    // Either more IDs could be retrieved, or more not-yet-shown details are known about
      && (!actionQueryCompleted(produceIdsKey, state) || currentViewLength < knownIds.filter(notSkipped).length)

    return <>
        { idsRenderedNow.length && props.optionalTitle || false }
        <ReactInfiniteScroller
            role="feed"
            initialLoad={ false }
            hasMore={ hasMore }
            loadMore={ loadMore }
            useWindow={ LISTEN_TO_WINDOW }
            loader={ <Loading key="Loading" /> }>
            {knownIds.filter(notSkipped).length === 0 && actionQueryCompleted(props.produceIds, state)
                ? !props.optionalTitle && <EmptySearchError message={ props.errorMessage } />
                : props.renderOneItemOnly && idsRenderedNow.length === 1
                    ? props.renderOneItemOnly(idsRenderedNow[0], 0)
                    : idsRenderedNow.map((id, i) => props.renderItem(id, i))}
        </ReactInfiniteScroller>
    </>
}

export default InfiniteScroll
